import { useCallback, useEffect, useRef, useState } from "react";

import { isScoutOptimisticConversationId } from "./scoutConversationIds";
import {
  SCOUT_NEW_CHAT_WRITE_MODE,
  SCOUT_WRITE_MODE_GQL,
  type ScoutWriteModeGql,
} from "./scoutWriteMode";

function writeModeForConversation(input: {
  conversationId: string | undefined;
  conversationWriteMode: ScoutWriteModeGql | undefined;
}): ScoutWriteModeGql {
  if (input.conversationWriteMode !== undefined) {
    return input.conversationWriteMode;
  }
  if (!input.conversationId) {
    return SCOUT_NEW_CHAT_WRITE_MODE;
  }
  return SCOUT_WRITE_MODE_GQL.OFF;
}

type UseScoutWriteModeInput = {
  conversationId: string | undefined;
  conversationWriteMode: ScoutWriteModeGql | undefined;
  /**
   * When true (default), reset to Ask whenever `conversationId` becomes
   * undefined (New chat / popover session clear), even if store writeMode is
   * briefly stale. Set false only if a host must keep local mode after clear.
   */
  resetWhenConversationClears?: boolean;
};

export function useScoutWriteMode(input: UseScoutWriteModeInput) {
  const { conversationId, conversationWriteMode, resetWhenConversationClears = true } = input;
  const [writeMode, setWriteMode] = useState<ScoutWriteModeGql>(() =>
    writeModeForConversation({ conversationId, conversationWriteMode })
  );
  const userTouchedRef = useRef(false);
  const lastConversationIdRef = useRef<string | undefined>(conversationId);

  useEffect(() => {
    const previousConversationId = lastConversationIdRef.current;
    lastConversationIdRef.current = conversationId;

    if (resetWhenConversationClears && !conversationId) {
      userTouchedRef.current = false;
      setWriteMode(SCOUT_NEW_CHAT_WRITE_MODE);
      return;
    }

    if (!previousConversationId) {
      // First send: undefined → optimistic id. Keep the user's Ask/Auto pick.
      if (conversationId !== undefined && isScoutOptimisticConversationId(conversationId)) {
        return;
      }

      // Empty /scout → existing thread (history click). Adopt stored mode.
      if (conversationId !== undefined && !isScoutOptimisticConversationId(conversationId)) {
        userTouchedRef.current = false;
        setWriteMode(writeModeForConversation({ conversationId, conversationWriteMode }));
      }
      return;
    }

    if (previousConversationId === conversationId) {
      return;
    }

    // Same in-flight thread: optimistic temp id → persisted uuid. Keep the
    // user's Ask/Auto selection until store/history writeMode catches up.
    if (
      userTouchedRef.current &&
      isScoutOptimisticConversationId(previousConversationId) &&
      conversationId !== undefined &&
      !isScoutOptimisticConversationId(conversationId)
    ) {
      return;
    }

    userTouchedRef.current = false;
    setWriteMode(writeModeForConversation({ conversationId, conversationWriteMode }));
  }, [conversationWriteMode, conversationId, resetWhenConversationClears]);

  useEffect(() => {
    if (userTouchedRef.current || conversationWriteMode === undefined) {
      return;
    }
    setWriteMode(conversationWriteMode);
  }, [conversationWriteMode]);

  const onWriteModeChange = useCallback((next: ScoutWriteModeGql) => {
    userTouchedRef.current = true;
    setWriteMode(next);
  }, []);

  // Existing thread whose stored mode has not hydrated yet — avoid flashing Off.
  const isWriteModeLoading =
    conversationId !== undefined && conversationWriteMode === undefined && !userTouchedRef.current;

  return { writeMode, onWriteModeChange, isWriteModeLoading };
}
